// ============================================================================
// LLM COSTS SERVICE - Frontend API client for LLM cost tracking via LiteLLM
// ============================================================================

import { api } from './api';

// ---- LiteLLM Project Usage (per-project costs) ----

export interface LiteLLMProjectUsage {
  projectId: string;
  period: string;
  periodSpend: number;
  allTimeSpend: number | null;
  totalRequests: number;
  tokens: { prompt: number; completion: number; total: number };
  byModel: Record<string, {
    spend: number;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    requests: number;
  }>;
  byDay: Array<{ date: string; spend: number; total_tokens: number; requests: number }>;
}

// ---- LiteLLM Admin Overview (cross-project) ----

export interface LiteLLMAdminOverview {
  totalSpend: number;
  totalRequests: number;
  byProject: Array<{ projectId: string; spend: number; requests: number; totalTokens: number }>;
  byModel: Array<{ model: string; spend: number; totalTokens: number; requests: number }>;
}

export async function getProjectCosts(projectId: string, period: string = '30d'): Promise<LiteLLMProjectUsage> {
  const response = await api.get(`/api/llm-costs/project/${projectId}`, { params: { period } });
  return response.data;
}

export async function getAdminCostOverview(): Promise<LiteLLMAdminOverview> {
  const response = await api.get('/api/llm-costs/admin/overview');
  return response.data;
}

// ---- Pricing Management (legacy — LiteLLM owns cost calculation) ----

export interface LLMPricingEntry {
  modelId: string;
  provider: string;
  displayName: string;
  inputPer1M: number;
  outputPer1M: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function getAllPricing(): Promise<LLMPricingEntry[]> {
  const response = await api.get('/api/llm-costs/pricing');
  return response.data.entries;
}

export async function upsertPricing(modelId: string, entry: Partial<LLMPricingEntry>): Promise<LLMPricingEntry> {
  const response = await api.put(`/api/llm-costs/pricing/${encodeURIComponent(modelId)}`, entry);
  return response.data.entry;
}

export async function deletePricing(modelId: string): Promise<void> {
  await api.delete(`/api/llm-costs/pricing/${encodeURIComponent(modelId)}`);
}

export async function seedPricing(): Promise<number> {
  const response = await api.post('/api/llm-costs/pricing/seed');
  return response.data.seeded;
}

// ---- Formatting Helpers ----

export function formatCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}

export function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return tokens.toString();
}
